// Vehicle Scheduler grid — rows = vehicles, columns = a hard 90-day window.
// Visual language matches TourTimeline (month runs, weekend bands, today line,
// sticky-left info column). A band's extent is the EVENT's ship-date window
// (single source of truth); bands on a named tour get an amber outline. Drag a
// band's outer edge to set the leave/return ship date (producers only).

import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import useVehicleScheduleStore, { RENTAL_VEHICLE_ID } from '../stores/useVehicleScheduleStore';
import { ScheduleVehicle, VehicleScheduleEvent } from './types';
import VehicleScheduleCell from './VehicleScheduleCell';
import VehicleBand, { BandContextMenuArgs, DRAG_BAND_MIME } from './VehicleBand';
import RentalBand from './RentalBand';
import ForecastStrip from './ForecastStrip';
import VehicleMaintBlock from './VehicleMaintBlock';
import { deriveVehicleBands, DerivedVehicleBand, MaintRun } from './band-runs';
import { DAY_W, INFO_W, ROW_H, hashHue } from './grid-constants';
import { enumerateDays } from '../utils/date-utils';

const STATUS_DOT: Record<string, string> = {
  ready: 'bg-green-500',
  flagged: 'bg-yellow-500',
  ooc: 'bg-red-500',
  checked_out: 'bg-blue-500',
  retired: 'bg-outline day:bg-gray-600',
};

/** dataTransfer key for dragging a Shows-row badge down onto a vehicle lane. */
const DRAG_EVENT_MIME = 'application/x-firefleet-event-id';

/** What a band-body drag carries. Mirrors the store's BandDragState minus the
 *  display-only fields; DRAG_BAND_MIME is defined alongside the band itself. */
export interface BandMovePayload {
  eventId: string;
  fromVehicleId: string;
  scope: 'event' | 'tour';
}

interface VehicleScheduleGridProps {
  days: string[];
  todayIdx?: number;
  canEdit: boolean;
  /** Forwarded to the scroll container so the page can manage scroll position. */
  scrollRef?: React.RefObject<HTMLDivElement>;
  onCellClick: (vehicleId: string, dateStr: string, rect: DOMRect) => void;
  onBandClick: (assignmentId: string) => void;
  onBandContextMenu: (args: BandContextMenuArgs) => void;
  /** Drop a Shows-row badge onto a vehicle lane → assign that event. */
  onAssignDrop: (vehicleId: string, eventId: string) => void;
  /** Drop a BAND onto a different vehicle lane → move it (whole tour if scoped). */
  onBandMoveDrop: (toVehicleId: string, payload: BandMovePayload) => void;
  /** Drop a Shows-row badge onto the rental lane → flag the event as a rental. */
  onRentalDrop: (eventId: string) => void;
  /** Left-click a Shipping / Rentals band → open the event detail modal. */
  onRentalBandClick: (eventId: string) => void;
  /** The Shipping / Rentals bands, packed into lanes. Derived once in the PAGE
   *  (`deriveRentalLanes`) because the cell popover's "already covered" check has
   *  to read the very same bands: a tour band spans stop 1 through stop N, and a
   *  travel day between two stops is inside the band but outside either stop's
   *  own window. */
  rentalLanes: DerivedVehicleBand[][];
  /** Hovering a band → show the calendar-style event card. */
  onBandHover: (event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => void;
  onBandHoverEnd: () => void;
}

const EMPTY_BANDS: DerivedVehicleBand[] = [];
const EMPTY_MAINT: MaintRun[] = [];

const VehicleScheduleGrid: React.FC<VehicleScheduleGridProps> = ({
  days, todayIdx, canEdit, scrollRef, onCellClick, onBandClick, onBandContextMenu, onAssignDrop, onBandMoveDrop, onRentalDrop, onRentalBandClick, rentalLanes, onBandHover, onBandHoverEnd,
}) => {
  const vehicles = useVehicleScheduleStore(s => s.vehicles);
  const events = useVehicleScheduleStore(s => s.events);
  const assignments = useVehicleScheduleStore(s => s.assignments);
  const unavailability = useVehicleScheduleStore(s => s.unavailability);
  const tours = useVehicleScheduleStore(s => s.tours);

  // Which events have transport sorted — an owned vehicle OR transport arranged
  // (shipped / drive-rental). Yellow is left for everything else: shows whose
  // transport hasn't been planned yet (incl. the default-'shipping' untouched ones).
  const assignedEventIds = useMemo(
    () => new Set([
      ...assignments.map(a => a.event_id),
      ...events.filter(e => e.transport_arranged).map(e => e.id),
    ]),
    [assignments, events]
  );

  const eventById = useMemo(() => {
    const m = new Map<string, typeof events[number]>();
    events.forEach(e => m.set(e.id, e));
    return m;
  }, [events]);

  const tourNameById = useMemo(() => {
    const m = new Map<string, string>();
    tours.forEach(t => m.set(t.id, t.name));
    return m;
  }, [tours]);

  // Bands per vehicle, extent = event ship window, with tour bookend drag flags.
  const bandsByVehicle = useMemo(
    () => deriveVehicleBands(assignments, eventById, tourNameById),
    [assignments, eventById, tourNameById]
  );

  // Maintenance ranges per vehicle (asset_id == vehicle row id).
  const maintByVehicle = useMemo(() => {
    const m = new Map<string, MaintRun[]>();
    for (const u of unavailability) {
      const arr = m.get(u.asset_id) || [];
      arr.push({ id: u.id, notes: u.notes || '', start: u.start_date, end: u.end_date });
      m.set(u.asset_id, arr);
    }
    return m;
  }, [unavailability]);

  // Per-day demand map — events whose date range covers each day. Drives the
  // read-only "Shows" header row (mirrors the crew grid's "Events on Day").
  const eventsPerDay = useMemo(() => {
    const map = new Map<string, typeof events>();
    for (const e of events) {
      if (e.status === 'cancelled') continue;
      const end = e.end_date || e.event_date;
      for (const ds of enumerateDays(e.event_date, end)) {
        const arr = map.get(ds) || [];
        arr.push(e);
        map.set(ds, arr);
      }
    }
    return map;
  }, [events]);

  // Every show is shown (no "+N more" hiding) so each stays draggable and its
  // status is visible. The row grows to fit the busiest day.
  const maxShowsPerDay = useMemo(() => {
    let m = 0;
    for (const arr of eventsPerDay.values()) if (arr.length > m) m = arr.length;
    return m;
  }, [eventsPerDay]);
  const showsRowHeight = Math.max(28, maxShowsPerDay * 14 + 6);

  const monthRuns = useMemo(() => {
    const runs: { label: string; count: number }[] = [];
    for (const d of days) {
      const label = dayjs.utc(d).format('MMM YYYY');
      const last = runs[runs.length - 1];
      if (last && last.label === label) last.count++;
      else runs.push({ label, count: 1 });
    }
    return runs;
  }, [days]);

  const totalWidth = days.length * DAY_W;

  const dayIdx = useMemo(() => {
    const m = new Map<string, number>();
    days.forEach((d, i) => m.set(d, i));
    return m;
  }, [days]);
  const windowStart = days[0] ?? '';
  const windowEnd = days[days.length - 1] ?? '';

  // Active fleet first, retired pushed to the bottom and dimmed.
  const orderedVehicles = useMemo(() => {
    const rank = (v: ScheduleVehicle) => (v.operational_status === 'retired' ? 1 : 0);
    return [...vehicles].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      const an = a._extension?.name ?? a.display_code;
      const bn = b._extension?.name ?? b.display_code;
      return an.localeCompare(bn);
    });
  }, [vehicles]);

  return (
    <div ref={scrollRef} className="overflow-x-auto border border-gray-700/60 day:border-gray-200 rounded-lg bg-gray-900 day:bg-white">
      <div style={{ width: totalWidth + INFO_W }} className="min-w-full">
        {/* ── Sticky header band: forecast + month + day-number + Shows demand row. ── */}
        <div className="sticky top-0 z-10">
        {/* ── Availability forecast strip (aligned to the day columns) ── */}
        <ForecastStrip days={days} todayIdx={todayIdx} totalWidth={totalWidth} />
        {/* ── Month header ── */}
        <div className="flex">
          <div
            className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200"
            style={{ width: INFO_W }}
          />
          <div className="relative h-6 bg-gray-900 day:bg-white border-b border-gray-700/60 day:border-gray-200" style={{ width: totalWidth }}>
            {(() => {
              let acc = 0;
              return monthRuns.map((run) => {
                const left = acc * DAY_W;
                acc += run.count;
                return (
                  <div
                    key={run.label}
                    className="absolute top-0 h-full flex items-center px-2 text-[11px] font-semibold text-on-surface-variant day:text-gray-700 border-l border-gray-700/40 day:border-gray-200"
                    style={{ left, width: run.count * DAY_W }}
                  >
                    {run.label}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* ── Day header ── */}
        <div className="flex">
          <div
            className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-3 py-1 text-[11px] text-on-surface-variant day:text-gray-500 font-medium flex items-end"
            style={{ width: INFO_W }}
          >
            Vehicles
          </div>
          <div className="relative h-7 border-b border-gray-700/60 day:border-gray-200" style={{ width: totalWidth }}>
            {days.map((d, i) => {
              const dd = dayjs.utc(d);
              const dow = dd.day();
              const isWeekend = dow === 0 || dow === 6;
              const isToday = i === todayIdx;
              return (
                <div
                  key={d}
                  className={`absolute top-0 h-full flex flex-col items-center justify-center text-[9px] leading-tight ${
                    isToday
                      ? 'text-primary-ink day:text-amber-700 font-bold bg-primary/10 day:bg-amber-100'
                      : isWeekend
                        ? 'text-on-surface-disabled day:text-gray-500 bg-gray-800/40 day:bg-gray-100'
                        : 'text-on-surface-variant day:text-gray-500'
                  }`}
                  style={{ left: i * DAY_W, width: DAY_W }}
                >
                  <span>{dd.format('dd').charAt(0)}</span>
                  <span>{dd.date()}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── "Shows" demand row — read-only list of events covering each day. ── */}
        <div className="flex">
          <div
            className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-3 py-1 text-[11px] text-on-surface-variant day:text-gray-500 font-medium flex items-center"
            style={{ width: INFO_W }}
          >
            Shows
          </div>
          <div className="relative bg-gray-900 day:bg-white border-b border-gray-700/60 day:border-gray-200" style={{ width: totalWidth, height: showsRowHeight }}>
            {days.map((d, i) => {
              const dow = dayjs.utc(d).day();
              const isWeekend = dow === 0 || dow === 6;
              const isToday = i === todayIdx;
              const dayEvents = eventsPerDay.get(d) || [];
              const shown = dayEvents;
              return (
                <div
                  key={d}
                  className={`absolute top-0 bottom-0 flex flex-col items-stretch justify-start gap-px py-0.5 px-0.5 ${
                    isToday ? 'bg-primary/5 day:bg-amber-50' : isWeekend ? 'bg-gray-800/40 day:bg-gray-100' : ''
                  }`}
                  style={{ left: i * DAY_W, width: DAY_W }}
                >
                  {shown.map((evt) => {
                    const assigned = assignedEventIds.has(evt.id);
                    return (
                      <div
                        key={evt.id}
                        draggable={canEdit}
                        onDragStart={canEdit ? (e) => {
                          e.dataTransfer.setData(DRAG_EVENT_MIME, evt.id);
                          e.dataTransfer.setData('text/plain', evt.name);
                          e.dataTransfer.effectAllowed = 'copy';
                        } : undefined}
                        className={`rounded-sm px-1 text-[9px] font-medium leading-tight truncate ${
                          assigned ? 'bg-green-600/80 day:bg-green-600 text-green-50' : 'bg-yellow-500/85 day:bg-yellow-400 text-yellow-950'
                        } ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        title={`${evt.name} — ${evt.ls_drone_count + evt.pyro_drone_count} drones · ${assigned ? 'vehicle assigned' : 'no vehicle yet'}${evt.tour_id ? '  ·  on a tour' : ''}${evt.location ? `\n${evt.location}` : ''}${canEdit ? '\nDrag onto a vehicle row to assign' : ''}`}
                      >
                        {evt.name.length > 8 ? evt.name.slice(0, 7) + '…' : evt.name}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        </div>

        {/* ── Vehicle rows ── */}
        {orderedVehicles.length === 0 && (
          <div className="flex">
            <div className="sticky left-0 shrink-0 bg-gray-850 day:bg-gray-50 border-r border-gray-700/60 day:border-gray-200" style={{ width: INFO_W }} />
            <div className="px-4 py-8 text-sm text-on-surface-variant day:text-gray-500">No vehicles in the fleet yet.</div>
          </div>
        )}
        {orderedVehicles.map((v) => (
          <VehicleRow
            key={v.id}
            vehicle={v}
            days={days}
            dayIdx={dayIdx}
            windowStart={windowStart}
            windowEnd={windowEnd}
            todayIdx={todayIdx}
            totalWidth={totalWidth}
            bands={bandsByVehicle.get(v.id) || EMPTY_BANDS}
            maintRuns={maintByVehicle.get(v.id) || EMPTY_MAINT}
            vehicleHue={hashHue(v.id)}
            canEdit={canEdit}
            onCellClick={onCellClick}
            onBandClick={onBandClick}
            onBandContextMenu={onBandContextMenu}
            onAssignDrop={onAssignDrop}
            onBandMoveDrop={onBandMoveDrop}
            onBandHover={onBandHover}
            onBandHoverEnd={onBandHoverEnd}
          />
        ))}

        {/* ── Rental lanes — ephemeral rentals packed into rows + 1 empty spare.
             Drag a Shows-row badge onto a lane to flag the event as a rental. ── */}
        <div className="flex">
          <div
            className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-100 border-y border-r border-gray-700/60 day:border-gray-200 px-3 py-1.5 text-[11px] text-on-surface-variant day:text-gray-700 font-semibold uppercase tracking-wider"
            style={{ width: INFO_W }}
            title="Shipped freight = teal · Drive-rental = sand"
          >
            <span className="text-cyan-300 day:text-cyan-700">Shipping</span>
            <span> / </span>
            <span className="text-orange-300 day:text-orange-700">Rentals</span>
          </div>
          <div className="bg-gray-900/40 day:bg-gray-50 border-y border-gray-700/60 day:border-gray-200" style={{ width: totalWidth }} />
        </div>
        {[...rentalLanes, []].map((laneBands, i) => (
          <RentalLaneRow
            key={`rental-lane-${i}`}
            laneIndex={i}
            bands={laneBands}
            days={days}
            dayIdx={dayIdx}
            windowStart={windowStart}
            windowEnd={windowEnd}
            todayIdx={todayIdx}
            totalWidth={totalWidth}
            canEdit={canEdit}
            onCellClick={onCellClick}
            onRentalDrop={onRentalDrop}
            onRentalBandClick={onRentalBandClick}
            onBandContextMenu={onBandContextMenu}
            onBandHover={onBandHover}
            onBandHoverEnd={onBandHoverEnd}
          />
        ))}
      </div>
    </div>
  );
};

/** One rental lane (a packed row of non-overlapping rental bands, or the empty
 *  spare). Drop target for flagging a show as a rental. */
const RentalLaneRow: React.FC<{
  laneIndex: number;
  bands: DerivedVehicleBand[];
  days: string[];
  dayIdx: Map<string, number>;
  windowStart: string;
  windowEnd: string;
  todayIdx?: number;
  totalWidth: number;
  canEdit: boolean;
  /** Same signature as the vehicle rows'; the lane passes RENTAL_VEHICLE_ID as
   *  the "vehicle", which is what tells the popover to offer transport-arranged
   *  instead of assign. */
  onCellClick: (vehicleId: string, dateStr: string, rect: DOMRect) => void;
  onRentalDrop: (eventId: string) => void;
  onRentalBandClick: (eventId: string) => void;
  onBandContextMenu: (args: BandContextMenuArgs) => void;
  onBandHover: (event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => void;
  onBandHoverEnd: () => void;
}> = ({ laneIndex, bands, days, dayIdx, windowStart, windowEnd, todayIdx, totalWidth, canEdit, onCellClick, onRentalDrop, onRentalBandClick, onBandContextMenu, onBandHover, onBandHoverEnd }) => {
  const [dropActive, setDropActive] = React.useState(false);
  const isSpare = bands.length === 0;
  // These lanes take a Shows-row badge, not a band being moved off a vehicle.
  // "Move to shipping / rentals" is a different operation (it clears the truck AND
  // sets transport_arranged) and lives on the band's right-click menu. Without
  // this the lane would light up green and then silently do nothing.
  const bandDrag = useVehicleScheduleStore(s => s.bandDrag);

  const dropProps = canEdit ? {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = bandDrag ? 'none' : 'copy';
    },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setDropActive(true); },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false); },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropActive(false);
      if (bandDrag) return;
      const id = e.dataTransfer.getData(DRAG_EVENT_MIME);
      if (id) onRentalDrop(id);
    },
  } : {};

  return (
    <div className="flex group">
      <div
        className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-3 py-1 flex items-center"
        style={{ width: INFO_W, height: ROW_H }}
      >
        <span className="text-sm font-medium text-on-surface-variant day:text-gray-600">Lane {laneIndex + 1}</span>
      </div>
      <div
        className={`relative border-b border-gray-700/40 day:border-gray-200 ${
          !dropActive ? '' : bandDrag
            ? 'ring-2 ring-inset ring-red-400/70 bg-red-500/10'
            : 'ring-2 ring-inset ring-green-400/70 bg-green-500/10'
        }`}
        style={{ width: totalWidth, height: ROW_H }}
        {...dropProps}
      >
        <GridBackdrop days={days} todayIdx={todayIdx} />
        {days.map((d, i) => (
          // Wired to the real handler. This was `noop`, which is why a lane could
          // only be filled by dragging — every other row in the grid opens the
          // picker on click, and there was no reason this one should not.
          <VehicleScheduleCell key={d} vehicleId={RENTAL_VEHICLE_ID} dateStr={d} left={i * DAY_W} onCellClick={onCellClick} />
        ))}
        {isSpare && (
          <div className="absolute inset-0 flex items-center pl-2 pointer-events-none text-[11px] text-on-surface-disabled day:text-gray-400 italic">
            {canEdit ? 'Drag a show here to put it on a rental' : 'No rentals'}
          </div>
        )}
        {bands.map((band) => (
          <RentalBand
            key={band.key}
            band={band}
            dayIdx={dayIdx}
            windowStart={windowStart}
            windowEnd={windowEnd}
            numDays={days.length}
            canEdit={canEdit}
            onContext={onBandContextMenu}
            onOpenDetail={onRentalBandClick}
            onHover={onBandHover}
            onHoverEnd={onBandHoverEnd}
          />
        ))}
      </div>
    </div>
  );
};

/** Weekend bands + today line behind a lane. */
const GridBackdrop: React.FC<{ days: string[]; todayIdx?: number }> = ({ days, todayIdx }) => (
  <>
    {days.map((d, i) => {
      const dow = dayjs.utc(d).day();
      if (dow !== 0 && dow !== 6) return null;
      return (
        <div
          key={d}
          className="absolute top-0 h-full bg-gray-800/30 day:bg-gray-200/70 pointer-events-none"
          style={{ left: i * DAY_W, width: DAY_W }}
        />
      );
    })}
    {todayIdx !== undefined && (
      <div
        className="absolute top-0 h-full w-px bg-primary/50 day:bg-amber-500/60 pointer-events-none"
        style={{ left: todayIdx * DAY_W + DAY_W / 2 }}
      />
    )}
  </>
);

const VehicleRow: React.FC<{
  vehicle: ScheduleVehicle;
  days: string[];
  dayIdx: Map<string, number>;
  windowStart: string;
  windowEnd: string;
  todayIdx?: number;
  totalWidth: number;
  bands: DerivedVehicleBand[];
  maintRuns: MaintRun[];
  vehicleHue: number;
  canEdit: boolean;
  onCellClick: (vehicleId: string, dateStr: string, rect: DOMRect) => void;
  onBandClick: (assignmentId: string) => void;
  onBandContextMenu: (args: BandContextMenuArgs) => void;
  onAssignDrop: (vehicleId: string, eventId: string) => void;
  onBandMoveDrop: (toVehicleId: string, payload: BandMovePayload) => void;
  onBandHover: (event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => void;
  onBandHoverEnd: () => void;
}> = React.memo(({
  vehicle, days, dayIdx, windowStart, windowEnd, todayIdx, totalWidth,
  bands, maintRuns, vehicleHue, canEdit, onCellClick, onBandClick, onBandContextMenu, onAssignDrop, onBandMoveDrop, onBandHover, onBandHoverEnd,
}) => {
  const ext = vehicle._extension;
  const unusable = ['ooc', 'retired'].includes(vehicle.operational_status);
  const isRetired = vehicle.operational_status === 'retired';
  const canDrag = canEdit && !unusable;
  // How many vertical sub-lanes this row needs (overlapping bands stack).
  const laneCount = bands.reduce((n, b) => Math.max(n, b.subLane + 1), 1);
  // Drop target highlight while dragging a Shows-row badge over this lane.
  const [dropActive, setDropActive] = React.useState(false);

  // A band-body drag in flight. Read from the store, not dataTransfer: the spec
  // forbids reading transfer data during dragover, which is exactly when the lane
  // needs to know whether it is a legal destination.
  const bandDrag = useVehicleScheduleStore(s => s.bandDrag);
  // Same vehicle is a no-op, not a move. A maintenance block over the band's
  // visible extent is the fast path for the guard the server enforces per show.
  const rejectsBandDrop = !!bandDrag && (
    bandDrag.fromVehicleId === vehicle.id ||
    maintRuns.some(m => m.start <= bandDrag.windowEnd && m.end >= bandDrag.windowStart)
  );

  const laneDropProps = canDrag ? {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = bandDrag ? (rejectsBandDrop ? 'none' : 'move') : 'copy';
    },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setDropActive(true); },
    onDragLeave: (e: React.DragEvent) => {
      // Only clear when the pointer actually leaves the lane (not child→child).
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropActive(false);
      const moveRaw = e.dataTransfer.getData(DRAG_BAND_MIME);
      if (moveRaw) {
        if (rejectsBandDrop) return;
        try {
          const payload = JSON.parse(moveRaw);
          if (payload?.eventId && payload?.fromVehicleId) onBandMoveDrop(vehicle.id, payload);
        } catch {
          // A malformed payload can only come from something that isn't our band.
        }
        return;
      }
      const id = e.dataTransfer.getData(DRAG_EVENT_MIME);
      if (id) onAssignDrop(vehicle.id, id);
    },
  } : {};

  // Green invites the drop, red says this lane will refuse it. Without the red
  // state a same-vehicle or blocked lane looks identical to a valid one and the
  // drop silently does nothing.
  const dropRing = !dropActive
    ? ''
    : rejectsBandDrop
      ? 'ring-2 ring-inset ring-red-400/70 bg-red-500/10'
      : 'ring-2 ring-inset ring-green-400/70 bg-green-500/10';

  return (
    <div className="flex group">
      {/* Sticky info cell */}
      <div
        className={`sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-3 py-1 flex flex-col justify-center gap-0.5 overflow-hidden ${
          isRetired ? 'opacity-50' : ''
        }`}
        style={{ width: INFO_W, height: ROW_H }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[vehicle.operational_status] || 'bg-outline day:bg-gray-500'}`}
            title={vehicle.operational_status.replace('_', ' ')}
          />
          <span
            className={`text-sm font-semibold leading-tight truncate ${unusable ? 'text-on-surface-variant day:text-gray-500 line-through' : 'text-white day:text-gray-900'}`}
            title={ext?.name ?? vehicle.display_code}
          >
            {ext?.name ?? vehicle.display_code}
          </span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-on-surface-variant day:text-gray-500 overflow-hidden whitespace-nowrap">
          {ext?.vehicle_class && <span className="capitalize">{ext.vehicle_class}</span>}
          {ext && (
            <span className="text-on-surface-variant day:text-gray-400">· {ext.crew_capacity ?? 0} seats</span>
          )}
          {ext?.can_tow && (
            <span className="inline-flex px-1 py-px rounded ring-1 ring-inset ring-sky-800/60 day:ring-sky-300 text-sky-300/90 day:text-sky-700 bg-sky-900/20 day:bg-sky-100">
              tows
            </span>
          )}
        </div>
      </div>

      {/* Timeline lane */}
      <div
        className={`relative border-b border-gray-700/40 day:border-gray-200 ${dropRing}`}
        style={{ width: totalWidth, height: ROW_H }}
        {...laneDropProps}
      >
        <GridBackdrop days={days} todayIdx={todayIdx} />

        {/* Day-cell layer — empty-day click target + drag pointer target. */}
        {days.map((d, i) => (
          <VehicleScheduleCell
            key={d}
            vehicleId={vehicle.id}
            dateStr={d}
            left={i * DAY_W}
            onCellClick={onCellClick}
          />
        ))}

        {/* Maintenance run blocks. */}
        {maintRuns.map((m) => (
          <VehicleMaintBlock
            key={m.id}
            run={m}
            dayIdx={dayIdx}
            windowStart={windowStart}
            windowEnd={windowEnd}
            numDays={days.length}
          />
        ))}

        {/* Assignment band run-blocks. */}
        {bands.map((band) => (
          <VehicleBand
            key={band.key}
            vehicleId={vehicle.id}
            band={band}
            dayIdx={dayIdx}
            windowStart={windowStart}
            windowEnd={windowEnd}
            numDays={days.length}
            vehicleHue={vehicleHue}
            subLane={band.subLane}
            laneCount={laneCount}
            canEdit={canEdit}
            canDrag={canDrag}
            onBandClick={onBandClick}
            onBandContextMenu={onBandContextMenu}
            onHover={onBandHover}
            onHoverEnd={onBandHoverEnd}
          />
        ))}
      </div>
    </div>
  );
});
VehicleRow.displayName = 'VehicleRow';

export default VehicleScheduleGrid;
